import { Button, Icon, Skeleton } from "@sagot/ui";
import { useState } from "react";
import type { Slot } from "./contract";
import { dateKey, formatDate, formatTime } from "./dates";
import { DEFAULT_ACTION_LABELS, type ActionLabels } from "./labels";
import { DayChips, TimeChips } from "./slot-chips";
import type { UseSlotsResult } from "./use-slots";

const SKELETON_DAYS = 5;
const SKELETON_TIMES = 6;

function PickerSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-busy="true" className="space-y-3">
      <span className="sr-only">{label}</span>
      <div className="flex gap-2">
        {Array.from({ length: SKELETON_DAYS }, (_, index) => (
          <Skeleton key={index} className="h-11 w-16 shrink-0" />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {Array.from({ length: SKELETON_TIMES }, (_, index) => (
          <Skeleton key={index} className="h-11" />
        ))}
      </div>
    </div>
  );
}

interface EmptyDaysProps {
  slots: UseSlotsResult;
  timeZone: string;
  locale?: string;
  labels: ActionLabels;
}

/** No open day in the loaded window: point at the next open slot, or offer more days. */
function EmptyDays({ slots, timeZone, locale, labels }: EmptyDaysProps) {
  const next = slots.nextAvailable;
  const nextDay = next ? formatDate(next.start, timeZone, locale) : "";
  return (
    <div className="rounded-md border border-dashed border-line-strong p-3 text-center">
      <Icon name="calendar" className="mx-auto size-5 text-fg-subtle" />
      <p className="mt-1 text-sm font-bold text-fg">{labels.noTimesTitle}</p>
      <p className="mt-0.5 text-xs text-fg-muted">{next ? labels.nextOpen(`${nextDay} · ${formatTime(next.start, timeZone, locale)}`) : labels.noTimesBody}</p>
      {next ? (
        <Button
          variant="secondary"
          className="mt-2 min-h-11"
          onClick={() => {
            slots.jumpTo(dateKey(new Date(next.start), timeZone));
          }}
        >
          {labels.jumpTo(nextDay)}
        </Button>
      ) : null}
      {!next && slots.canLoadMore ? (
        <Button variant="secondary" className="mt-2 min-h-11" onClick={slots.loadMore} loading={slots.loadingMore}>
          {labels.showMoreDays}
        </Button>
      ) : null}
    </div>
  );
}

function InlineError({ labels, onRetry }: { labels: ActionLabels; onRetry: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-danger-soft p-3 text-xs text-fg">
      <Icon name="alert-triangle" className="size-4 text-danger" />
      <span className="font-semibold">{labels.slotsError}</span>
      <Button variant="secondary" size="sm" className="min-h-11" onClick={onRetry}>
        <Icon name="refresh-cw" />
        {labels.retry}
      </Button>
    </div>
  );
}

export interface SlotPickerProps {
  slots: UseSlotsResult;
  timeZone: string;
  locale?: string;
  selectedId: string | null;
  onSelect: (slot: Slot) => void;
  /** English by default. */
  labels?: ActionLabels;
  disabled?: boolean;
}

/** Days → times in the visitor's zone (roving focus), "Show more days", skeleton, empty and error states. */
export function SlotPicker({ slots, timeZone, locale, selectedId, onSelect, labels = DEFAULT_ACTION_LABELS, disabled }: SlotPickerProps) {
  const [chosenDate, setChosenDate] = useState<string | null>(null);
  const { days } = slots;
  const selectedDate = days.find((day) => day.slots.some((slot) => slot.id === selectedId))?.date;
  const active = days.find((day) => day.date === (chosenDate ?? selectedDate)) ?? days[0];

  if (slots.status === "loading") return <PickerSkeleton label={labels.loadingTimes} />;
  if (slots.status === "error") return <InlineError labels={labels} onRetry={slots.retry} />;
  if (!active) return <EmptyDays slots={slots} timeZone={timeZone} locale={locale} labels={labels} />;
  return (
    <div className="space-y-3">
      <DayChips days={days} activeDate={active.date} locale={locale} labels={labels} disabled={disabled} onSelect={setChosenDate} />
      <TimeChips date={active.date} slots={active.slots} selectedId={selectedId} timeZone={timeZone} locale={locale} labels={labels} disabled={disabled} onSelect={onSelect} />
      <p className="text-[11px] text-fg-subtle">{labels.timesIn(timeZone)}</p>
      {slots.partialError ? <InlineError labels={labels} onRetry={slots.retry} /> : null}
      {slots.canLoadMore ? (
        <Button variant="ghost" size="sm" className="min-h-11" onClick={slots.loadMore} loading={slots.loadingMore} disabled={disabled}>
          {slots.loadingMore ? labels.loadingMoreDays : labels.showMoreDays}
        </Button>
      ) : null}
    </div>
  );
}
